import type { Account } from '../types/api';
import { accountLabel } from './account-label';

/**
 * Ordering and labelling for "which account" pickers on Bills.
 *
 * A funding account is almost always chequing or savings, so assets come
 * first; cards and loans follow because a bill paid through Chexy is charged
 * to a card. Within each group the signed-in member's own accounts lead --
 * the October bill run could not pick PRIVATE BANKING ••5781 precisely
 * because it was missing, and when everything is present it should also be
 * near the top.
 */

/** Group heading for a picker option. */
export function accountGroup(a: Pick<Account, 'account_type'>): string {
  switch (a.account_type) {
    case 'Asset': return 'Chequing & savings';
    case 'Debt': return 'Cards & loans';
    default: return 'Investments';
  }
}

const TYPE_RANK: Record<string, number> = { Asset: 0, Debt: 1, Investment: 2 };

/**
 * Active accounts of the given types, sorted for a picker.
 *
 * @param accounts Every household account.
 * @param types Account types to offer, e.g. ['Asset', 'Debt'].
 * @param currentUserId The signed-in member, whose accounts sort first.
 * @returns A new, sorted array.
 */
export function pickerAccounts(
  accounts: Account[], types: Account['account_type'][], currentUserId?: string,
): Account[] {
  const allowed = new Set(types);
  return accounts
    .filter((a) => a.is_active !== false && allowed.has(a.account_type))
    .sort((a, b) => {
      const t = (TYPE_RANK[a.account_type] ?? 9) - (TYPE_RANK[b.account_type] ?? 9);
      if (t !== 0) return t;
      const mine = (x: Account) => (currentUserId && x.owner_user_id === currentUserId ? 0 : 1);
      const m = mine(a) - mine(b);
      if (m !== 0) return m;
      return accountLabel(a).localeCompare(accountLabel(b));
    });
}

/** Who owns it, for the option's secondary line. */
export function ownerLabel(a: Account, currentUserId?: string): string {
  if (currentUserId && a.owner_user_id === currentUserId) return 'You';
  if (a.is_joint) return 'Joint';
  return a.owner_email?.split('@')[0] || 'Other member';
}

/** Everything a search box should match against. */
export function accountSearchText(a: Account, currentUserId?: string): string {
  return [
    accountLabel(a), a.name, a.institution, a.account_number_last4,
    ownerLabel(a, currentUserId), accountGroup(a),
  ].filter(Boolean).join(' ').toLowerCase();
}
